import React from 'react';
import { AutoComplete as AntdAutoComplete } from 'antd';
import type { AutoCompleteProps as AntdAutoCompleteProps } from 'antd/es/auto-complete';
import type { RefSelectProps } from 'antd/es/select';
import classnames from 'classnames';
import cn from './styles.module.scss';

export type AutoCompleteProps = AntdAutoCompleteProps & {
  /**
   * Custom input element. Pass `<Input.TextArea />`, `<Input.Search />`, or
   * any antd input-compatible element to override the default antd input.
   * Visual props (`status`, `size`, `variant`, `disabled`) on the wrapper
   * are forwarded into the child as defaults — child's own props still win.
   */
  children?: React.ReactElement;
};

export const AutoComplete = React.forwardRef<RefSelectProps, AutoCompleteProps>(
  ({ className, children, ...props }, ref) => {
    // Antd's AutoComplete clones a custom child input but does NOT propagate
    // `status` / `size` / `variant` / `disabled` to it. When children are
    // provided, we forward those four visual props ourselves so the child's
    // border/sizing matches the wrapper. Without children, antd renders its
    // own internal input which already picks up these props correctly.
    const enhancedChild = children
      ? React.cloneElement(children, {
          status: props.status,
          size: props.size,
          variant: props.variant,
          disabled: props.disabled,
          ...children.props,
        })
      : null;

    return (
      <AntdAutoComplete
        ref={ref}
        className={classnames(cn.autocomplete, className)}
        {...props}
      >
        {enhancedChild}
      </AntdAutoComplete>
    );
  },
);

AutoComplete.displayName = 'AutoComplete';
